import { Box, Flex, Progress, Stack, Text } from "@chakra-ui/react";
import { McpUsageSummaryRO } from "@intentsify/dto";
import { formatBillingDate } from "@intentsify/utils";
import { formatNumber, usageColorScheme } from "./McpUsage.utils";

type McpUsageSummaryViewProps = Pick<
  McpUsageSummaryRO,
  "totalCredits" | "usedCredits" | "availableCredits" | "billableYear"
>;

const Stat = ({ label, value }: { label: string; value: number }) => (
  <Box>
    <Text color="gray.500" fontSize="sm">
      {label}
    </Text>
    <Text fontSize="lg" fontWeight="semibold">
      {formatNumber(value)}
    </Text>
  </Box>
);

const McpUsageSummaryView = ({
  totalCredits,
  usedCredits,
  availableCredits,
  billableYear,
}: McpUsageSummaryViewProps) => {
  const isProvisioned = billableYear !== null && totalCredits > 0;
  const pct = totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;
  const colorScheme = usageColorScheme(pct);

  return (
    <Stack spacing={4}>
      <Flex gap={8} align="flex-end">
        <Stat label="Total Credits" value={totalCredits} />
        <Stat label="Used" value={usedCredits} />
        <Stat label="Available" value={availableCredits} />
        {!isProvisioned && (
          <Text color="gray.500" fontSize="sm">
            Not provisioned
          </Text>
        )}
      </Flex>
      <Box data-testid="mcp-usage-bar" data-color-scheme={colorScheme}>
        <Progress
          aria-label="MCP credits used"
          colorScheme={colorScheme}
          value={Math.min(usedCredits, totalCredits)}
          max={totalCredits > 0 ? totalCredits : 1}
          borderRadius="md"
        />
      </Box>
      {billableYear && (
        <Text color="gray.500" fontSize="sm">
          Billable year · {formatBillingDate(billableYear.start)} –{" "}
          {formatBillingDate(billableYear.end)}
        </Text>
      )}
    </Stack>
  );
};

export { McpUsageSummaryView };
